import { detectIE } from './browserHelpers';
import { API, ErrorBodyResponse } from '../types';

export interface AjaxRequest {
  method: 'GET' | 'POST';
  url: API | string;
  data?: { [key: string]: any };
  fields?: RequestInit;
  headers?: { [key: string]: string };
  isJson?: boolean;
  timeStamp?: boolean;
  handleError?: (response: Response, body: ErrorBodyResponse) => void;
}

export default function ajaxRequest<T = any>({
  method,
  url,
  data,
  fields = {},
  isJson = true,
  timeStamp = false,
  headers = {},
  handleError = () => {}
}: AjaxRequest): Promise<T> {
  const addTimeStamp = timeStamp || detectIE();
  const mappedUrl =
    method === 'GET' && data
      ? `${url}?${toQueryParams(data)}${addTimeStamp ? `&t=${Date.now()}` : ''}`
      : `${url}${addTimeStamp ? `${url.includes('?') ? '&' : '?'}t=${Date.now()}` : ''}`;
  headers['X-Requested-With'] = 'XMLHttpRequest';

  if (method === 'POST') {
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
  }
  return fetch(mappedUrl, {
    method,
    credentials: 'same-origin',
    ...(data && method === 'POST'
      ? {
          headers,
          body: toBodyParams(data)
        }
      : { headers }),
    ...fields
  })
    .then((response) => status(response, handleError))
    .then(isJson ? json : text);
}

export function ajaxRequestWithRetryIntervals<T = any>(requestInfo: AjaxRequest, attempt = 0): Promise<T> {
  const data = requestInfo.data || {};
  return ajaxRequest({ ...requestInfo, data: { ...data, attempt } }).then((res) => {
    if (res.status === 'SUCCESS') {
      return res;
    }
    if (res.status === 'RETRY_ERROR' && attempt < res.retryIntervals.length) {
      return new Promise((resolve, reject): void => {
        setTimeout(
          () =>
            ajaxRequestWithRetryIntervals(requestInfo, attempt + 1)
              .then(resolve)
              .catch(reject),
          res.retryIntervals[attempt] * 1000
        );
      });
    }
    return Promise.reject(res);
  });
}

function status(response: Response, handleError): Promise<Response> {
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }

  return response.json().then((body: ErrorBodyResponse) => {
    handleError(response, body);
    return Promise.reject(new Error(response.statusText));
  });
}

function json(response: Response): Promise<any> {
  return response.json();
}

function text(response: Response): Promise<any> {
  return response.text();
}

function toBodyParams(data: { [key: string]: any }): URLSearchParams {
  const searchParams = new URLSearchParams();
  Object.keys(data).forEach((key) => searchParams.set(key, data[key]));
  return searchParams;
}

export function toQueryParams(data: { [key: string]: any }): string {
  return Object.keys(data)
    .filter((key) => !!data[key])
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
}
